import { Card, CardContent, Typography, Box } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import styles from "./IssuesBreakdownCard.module.scss";
import { useTranslation } from "react-i18next";

const REDS = ["#D93025", "#EA4335", "#F28B82"];

const renderLabel = ({ name, cx, cy, midAngle, outerRadius }) => {
    if (!name) return null;
    const RAD = Math.PI / 180;
    const r = outerRadius + 22;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
        <text
            x={x}
            y={y}
            className={styles.labelText}
            textAnchor={x > cx ? "start" : "end"}
            dominantBaseline="central"
        >
            {name}
        </text>
    );
};

const IssuesBreakdownCard = ({ data = [], topIssues = [] }) => {
    const { t } = useTranslation();
    const donutData = (data || []).map((item, idx) => ({
        name: item.category,
        value: item.count,
        color: REDS[idx % REDS.length],
    }));
    const total = donutData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

    const normalizedTopIssues = (topIssues || []).map((t, i) => ({
        label: t?.label ?? t?.issueText ?? t?.issue_text ?? String(t ?? ""),
        occurrences: t?.occurrences ?? t?.count ?? null,
        color: REDS[i % REDS.length],
    }));

    const displayTopIssues =
        normalizedTopIssues.length > 0
            ? normalizedTopIssues
            : (donutData || []).map((d, i) => ({
                label: d.name,
                occurrences: d.value ?? null,
                color: REDS[i % REDS.length],
            }));

    return (
        <Card className={styles.card}>
            <CardContent className={styles.cardContent}>
                <Typography variant="subtitle3" className={styles.title}>
                    {t("reports.patientSurveyReportPage.issues.breakdownTitle")}
                </Typography>

                <div className={styles.chartWrapper}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 8, right: 64, bottom: 8, left: 64 }}>
                            <Tooltip
                                contentStyle={{
                                    fontSize: 12,
                                    borderRadius: 8,
                                    border: "1px solid #E5E7EB",
                                }}
                                formatter={(value, _name, { payload }) => {
                                    const v = Number(value) || 0;
                                    const pct = total ? Math.round((v / total) * 100) : 0;
                                    return [`${v} responses (${pct}%)`, payload?.name ?? ""];
                                }}
                            />
                            <Pie
                                data={donutData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={54}
                                outerRadius={108}
                                paddingAngle={0.4}
                                stroke="#fff"
                                strokeWidth={0.4}
                                isAnimationActive={false}
                                label={renderLabel}
                                labelLine={{ stroke: "#D1D5DB", strokeWidth: 1 }}
                            >
                                {donutData.map((d, i) => (
                                    <Cell key={i} fill={d.color} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <Typography variant="subtitle2" className={styles.subtitle}>
                    {t("reports.patientSurveyReportPage.issues.topIssues")}:
                </Typography>

                <div className={styles.topIssuesList}>
                    {displayTopIssues.map((issue, idx) => (
                        <div key={idx} className={styles.topIssueItem}>
                            <div
                                className={styles.topIssueColor}
                                style={{ backgroundColor: issue.color }}
                            />
                            <Typography variant="body2" className={styles.topIssueLabel}>
                                {issue.label}
                            </Typography>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export default IssuesBreakdownCard;
